import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { chatApi, authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isMasterSandboxUser } from '../lib/testOverride';
import { resolveMentorFromBio } from '../lib/voiceProfile';
import {
  cancelSpeechPlayback,
  prepareSpeechAudio,
  resetAudioPlaybackGate,
  speakMentorReply,
  unlockSpeechPlayback,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import { sanitizeAssistantMessages, sanitizeMentorOutput } from '../lib/sanitizeMentorOutput';
import './LivingRoom.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');
const DEFAULT_MENTOR_VOICE_ID = 's116Gqp9aiAQAe29Albd';
const SETUP_DISPLAY_NAME_KEY = 'mentology.setupDisplayName';

interface Message {
  role: string;
  content: string;
}

interface Bio {
  preferred_name?: string;
  name?: string;
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
}

export const PRIVATE_REFLECTION_HOOKS: string[] = [];
export const CORPORATE_REFLECTION_HOOKS: string[] = [];

export const BRIEFING_ROOM_WELCOME =
  "Welcome to the Briefing Room, [Mentee Name]. I am AI-Mentor, and this is your private, high-focus business development space. There is no passive listening here — I will mirror your logic, challenge your assumptions, and hold you accountable to your own insights. Tell me: what is the primary challenge or breakthrough you bring to today's session, and what do you want to achieve from this mentoring relationship?";

/** @deprecated Prefer confirmDeleteAccount() from ../lib/deleteAccount */
export { DELETE_ACCOUNT_WARNING as DELETE_FILES_CONFIRM_MESSAGE } from '../lib/deleteAccount';
export { confirmDeleteAccount } from '../lib/deleteAccount';

interface LivingRoomConsoleProps {
  screenId: string;
  welcomeScript: string;
  reflectionHooks?: string[];
  menuContent: (closeMenu: () => void) => ReactNode;
  qrModal?: ReactNode;
  documentUploadModal?: ReactNode;
  assetBlocks?: ReactNode;
}

function readPendingBio(): Bio | null {
  try {
    const raw = sessionStorage.getItem('mentology.pendingBio');
    return raw ? JSON.parse(raw) as Bio : null;
  } catch {
    return null;
  }
}

function readStoredSetupName() {
  try {
    return localStorage.getItem(SETUP_DISPLAY_NAME_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

export function rememberSetupDisplayName(bio: {
  preferred_name?: string | null;
  name?: string | null;
}) {
  const display = bio.preferred_name?.trim() || '';
  try {
    if (display) {
      localStorage.setItem(SETUP_DISPLAY_NAME_KEY, display);
    } else {
      localStorage.removeItem(SETUP_DISPLAY_NAME_KEY);
    }
  } catch {
    // ignore storage failures
  }
}

function resolveMenteeName(bio: Bio | null, _userName?: string) {
  // Greeting uses Preferred Name only (Setup field) — not legal Name / surname.
  const fromBio = bio?.preferred_name?.trim();
  if (fromBio) return fromBio;

  const pending = readPendingBio();
  const fromPending = pending?.preferred_name?.trim();
  if (fromPending) return fromPending;

  const stored = readStoredSetupName();
  if (stored) return stored;

  return '';
}

function personalizeWelcomeScript(script: string, bio: Bio | null, userName?: string) {
  const name = resolveMenteeName(bio, userName);
  const displayName = name || 'your mentee';
  return script
    .replace(/\[Mentee(?:'s)? Name\]/gi, displayName)
    .replace(/I am your AI Mentor \[Mentor Name\],/gi, 'I am AI-Mentor,')
    .replace(/\[Mentor Name\]/gi, 'AI-Mentor');
}

function estimateSpeechDurationMs(script: string) {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1200, (words / 150) * 60 * 1000);
}

function splitSentences(script: string): string[] {
  const matches = script.match(/[^.?!]+[.?!]+(?:\s+|$)|[^.?!]+$/g);
  if (!matches?.length) return [script.trim()].filter(Boolean);
  return matches.map((part) => part.trim()).filter(Boolean);
}

export default function LivingRoomConsole({
  screenId,
  welcomeScript,
  reflectionHooks = [],
  menuContent,
  qrModal,
  documentUploadModal,
  assetBlocks,
}: LivingRoomConsoleProps) {
  const { user } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  const syncRafRef = useRef<number | null>(null);
  const playbackStarted = useRef(false);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const mentorVoiceIdRef = useRef(DEFAULT_MENTOR_VOICE_ID);
  const languageTargetRef = useRef<string | undefined>(undefined);

  const [menuOpen, setMenuOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [displayWelcome, setDisplayWelcome] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [consoleFrozen, setConsoleFrozen] = useState(false);
  const [freezeMessage, setFreezeMessage] = useState('');

  const stopSyncAnimation = useCallback(() => {
    if (syncRafRef.current != null) {
      cancelAnimationFrame(syncRafRef.current);
      syncRafRef.current = null;
    }
  }, []);

  const finishIntro = useCallback((script: string) => {
    stopSyncAnimation();
    setDisplayWelcome(script);
    setIntroComplete(true);
  }, [stopSyncAnimation]);

  const syncChunkTypewriter = useCallback((
    fullScript: string,
    chunkText: string,
    chunkStart: number,
    audio: HTMLAudioElement,
  ) => {
    const tick = () => {
      const durationSec = audio.duration && Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : estimateSpeechDurationMs(chunkText) / 1000;
      const progress = Math.min(1, (audio.currentTime || 0) / Math.max(durationSec, 0.01));
      const chunkChars = Math.max(1, Math.ceil(progress * chunkText.length));
      setDisplayWelcome(fullScript.slice(0, chunkStart + chunkChars));

      if (progress < 1 && !audio.ended && !audio.paused) {
        syncRafRef.current = requestAnimationFrame(tick);
        return;
      }

      setDisplayWelcome(fullScript.slice(0, chunkStart + chunkText.length));
      syncRafRef.current = null;
    };

    stopSyncAnimation();
    syncRafRef.current = requestAnimationFrame(tick);
  }, [stopSyncAnimation]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      stopSyncAnimation();
      if (introAudioRef.current) {
        introAudioRef.current.pause();
        introAudioRef.current = null;
      }
      cancelSpeechPlayback();
      playbackStarted.current = false;
    };
  }, [stopSyncAnimation]);

  useEffect(() => {
    let cancelled = false;
    const pollFreeze = () => {
      chatApi.consoleState()
        .then((state) => {
          if (cancelled) return;
          if (state.pendingThaw) {
            setConsoleFrozen(false);
            setFreezeMessage('');
            return;
          }
          setConsoleFrozen(!!state.frozen);
          setFreezeMessage(state.systemMessage || '');
        })
        .catch(() => {});
    };
    pollFreeze();
    const interval = setInterval(pollFreeze, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;
    playbackStarted.current = false;
    stopSyncAnimation();
    cancelSpeechPlayback();

    let script = personalizeWelcomeScript(welcomeScript, null, user?.name);
    let voiceId = DEFAULT_MENTOR_VOICE_ID;
    let languageTarget: string | undefined;
    let sentences = splitSentences(script);
    let firstAudioPromise: Promise<HTMLAudioElement | null> | null = null;
    let historyHasMessages = false;

    // Master-sandbox preview: seed Preferred Name if setup defaults are active.
    if (!resolveMenteeName(null, user?.name) && isMasterSandboxUser(user)) {
      rememberSetupDisplayName({ preferred_name: 'John', name: 'John Citizen' });
      script = personalizeWelcomeScript(welcomeScript, null, user?.name);
      sentences = splitSentences(script);
    }

    const historyPromise = chatApi.getHistory().catch(() => ({ messages: [] as Message[] })).then((historyData) => {
      historyHasMessages = (historyData.messages || []).length > 0;
      if (historyHasMessages && !cancelled) {
        setMessages(sanitizeAssistantMessages(historyData.messages || []));
        setDisplayWelcome(script);
        setIntroComplete(true);
        playbackStarted.current = true;
        cancelSpeechPlayback();
        stopSyncAnimation();
      }
      return historyData;
    });

    const bioPromise = authApi.getBio().catch(() => ({ bio: null as Bio | null })).then((bioData) => {
      if (cancelled) return bioData;
      if (bioData.bio) {
        rememberSetupDisplayName(bioData.bio);
      }
      script = personalizeWelcomeScript(welcomeScript, bioData.bio, user?.name);
      const mentorProfile = resolveMentorFromBio(bioData.bio);
      voiceId = mentorProfile.voiceId || DEFAULT_MENTOR_VOICE_ID;
      languageTarget = bioData.bio?.language_target ?? undefined;
      mentorVoiceIdRef.current = voiceId;
      languageTargetRef.current = languageTarget;
      sentences = splitSentences(script);
      // First sentence only — much faster than full-script TTS.
      firstAudioPromise = prepareSpeechAudio(
        voiceId,
        sentences[0] || script,
        languageTarget,
        undefined,
        { cancelActive: false },
      ).catch(() => null);
      return bioData;
    });

    const runChunkedIntro = async () => {
      if (cancelled || playbackStarted.current || historyHasMessages) return;
      playbackStarted.current = true;

      setDisplayWelcome('');
      setIntroComplete(false);
      resetAudioPlaybackGate();
      stopSyncAnimation();

      let charOffset = 0;
      let nextAudioPromise: Promise<HTMLAudioElement | null> | null = firstAudioPromise;

      for (let index = 0; index < sentences.length; index += 1) {
        if (cancelled || historyHasMessages) return;

        const chunk = sentences[index];
        const audio = nextAudioPromise
          ? await nextAudioPromise
          : await prepareSpeechAudio(voiceId, chunk, languageTarget, undefined, { cancelActive: false }).catch(() => null);
        nextAudioPromise = null;

        if (!audio) {
          charOffset += chunk.length + (index + 1 < sentences.length ? 1 : 0);
          setDisplayWelcome(script.slice(0, Math.min(script.length, charOffset)));
          continue;
        }

        const foundAt = script.indexOf(chunk, charOffset);
        const chunkStart = foundAt >= 0 ? foundAt : charOffset;
        charOffset = chunkStart;

        introAudioRef.current = audio;

        // Prefetch next sentence only after current audio begins (serialized voice queue).
        if (index + 1 < sentences.length) {
          nextAudioPromise = prepareSpeechAudio(
            voiceId,
            sentences[index + 1],
            languageTarget,
            undefined,
            { cancelActive: false },
          ).catch(() => null);
        }

        try {
          // Play first, then lock typewriter to audio.currentTime (same sync model as before).
          await audio.play();
          syncChunkTypewriter(script, chunk, chunkStart, audio);
          await new Promise<void>((resolve) => {
            if (audio.ended) {
              resolve();
              return;
            }
            audio.onended = () => resolve();
          });
        } catch (error) {
          console.warn('[Briefing chunk play]', error);
          setDisplayWelcome(script.slice(0, chunkStart + chunk.length));
        }

        charOffset = chunkStart + chunk.length;
        while (charOffset < script.length && /\s/.test(script[charOffset])) {
          charOffset += 1;
        }
        setDisplayWelcome(script.slice(0, charOffset));
      }

      if (!cancelled && !historyHasMessages) finishIntro(script);
    };

    // Start as soon as bio resolves; history can cancel if prior messages exist.
    bioPromise.then(async () => {
      if (cancelled || historyHasMessages) return;
      await new Promise((resolve) => setTimeout(resolve, 40));
      if (cancelled || historyHasMessages || playbackStarted.current) return;
      await runChunkedIntro();
    }).catch((error) => {
      console.warn('[Briefing intro]', error);
      if (!cancelled && !historyHasMessages) {
        setDisplayWelcome(script);
        setIntroComplete(true);
      }
    });

    void historyPromise;

    return () => {
      cancelled = true;
    };
  }, [welcomeScript, user?.name, user?.masterTestingOverride, stopSyncAnimation, syncChunkTypewriter, finishIntro]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [displayWelcome, messages, loading, introComplete]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    unlockSpeechPlayback();
    if (introAudioRef.current) {
      introAudioRef.current.pause();
      introAudioRef.current = null;
    }
    cancelSpeechPlayback();
    const userMsg = input.trim();
    setInput('');
    setMessages((current) => [...current, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const result = await chatApi.sendMessage(userMsg);
      if (result.crisis) {
        setConsoleFrozen(true);
        setFreezeMessage(result.message || result.injectedMessage || '');
        setMessages((current) => [...current, { role: 'system', content: sanitizeMentorOutput(result.message) }]);
        return;
      }
      const spoken = sanitizeMentorOutput(result.message.content);
      setMessages((current) => [
        ...current,
        { role: 'assistant', content: spoken },
      ]);
      void speakMentorReply(
        spoken,
        mentorVoiceIdRef.current || DEFAULT_MENTOR_VOICE_ID,
        languageTargetRef.current,
      ).catch((error) => {
        console.warn('[Briefing reply voice]', error);
      });
    } catch (err: unknown) {
      const e = err as { error?: string };
      setMessages((current) => [
        ...current,
        { role: 'system', content: e.error || 'Message failed' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = () => {
    const win = window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionInstance;
      webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    };
    type SpeechRecognitionInstance = {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    };

    const SpeechRecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInput(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const applyReflectionHook = (prompt: string) => {
    setInput((current) => (current.trim() ? `${current.trim()} ${prompt}` : prompt));
  };

  return (
    <div className={`living-room-screen${consoleFrozen ? ' workspace-locked' : ''}`} data-screen-id={screenId}>
      <header className="living-room-top-bar">
        <div className="living-room-brand">
          <img
            src={LOGO}
            alt="Mentology"
            className="living-room-logo brand-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <h1 className="living-room-title">Briefing Room</h1>
        </div>
        <div className="living-room-util" ref={menuRef}>
          <button
            type="button"
            className="living-room-util-trigger"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
          >
            Menu ▾
          </button>
          {menuOpen && (
            <div className="living-room-util-dropdown">
              {menuContent(() => setMenuOpen(false))}
            </div>
          )}
        </div>
      </header>

      <div className="living-room-body">
        {assetBlocks}
        <div
          ref={streamRef}
          className={`living-room-stream ${SPOKEN_TEXT_ATTRS.className}`}
        >
          <div className={`living-room-welcome scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
            {displayWelcome}
            {!introComplete && <span className="living-room-cursor">|</span>}
          </div>
          <div className="living-room-thread">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`living-room-bubble living-room-bubble-${message.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
              >
                {message.content}
              </div>
            ))}
            {loading && (
              <p className={`living-room-bubble scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
                Processing...
              </p>
            )}
          </div>
        </div>

        {reflectionHooks.length > 0 && (
          <div className="living-room-reflection-hooks">
            {reflectionHooks.map((hook) => (
              <button
                key={hook}
                type="button"
                className="living-room-reflection-hook"
                onClick={() => applyReflectionHook(hook)}
              >
                {hook}
              </button>
            ))}
          </div>
        )}
      </div>

      {consoleFrozen && (
        <div className="living-room-freeze-overlay" role="alert">
          <p className="living-room-freeze-title">Console Frozen — RED Email Protocol Active</p>
          <p className="living-room-freeze-message">{freezeMessage}</p>
        </div>
      )}

      <form
        className="living-room-input-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          if (input.trim() && !loading && !consoleFrozen) sendMessage();
        }}
      >
        <textarea
          className="living-room-chat-input"
          placeholder={consoleFrozen ? 'Console frozen pending mentor review...' : 'Share your challenge, reflection, or response...'}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (input.trim() && !loading && !consoleFrozen) sendMessage();
            }
          }}
          disabled={loading || consoleFrozen}
          aria-label="Mentee message input"
        />
        <div className="living-room-input-controls">
          <button
            type="submit"
            className="living-room-send-btn"
            disabled={loading || !input.trim() || consoleFrozen}
          >
            Send
          </button>
          <button
            type="button"
            className={`living-room-mic-btn ${listening ? 'living-room-mic-active' : ''}`}
            onClick={toggleMic}
            disabled={loading || consoleFrozen}
            aria-label="Microphone"
            title="Microphone"
          >
            🎙️
          </button>
        </div>
      </form>

      {qrModal}
      {documentUploadModal}
    </div>
  );
}
